import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ReposicionService } from 'src/app/pages/operations/reposicion/reposicion.service';
import { BranchStoreService } from 'src/app/pages/operations/reposicion/components/branch-store.service';
import { TablaMedicamentosService } from 'src/app/pages/operations/reposicion/components/reposicion-asistida/medicamentos-reponer/tabla-medicamentos/tabla-medicamentos.service';

import {
  Medicamento,
  Inventario,
  ProductoAPI
} from 'src/app/pages/operations/reposicion/models/reposicion.model';

@Component({
  selector: 'app-tabla-medicamentos',
  templateUrl: './tabla-medicamentos.component.html',
  styleUrls: ['./tabla-medicamentos.component.css']
})
export class TablaMedicamentosComponent implements OnInit, OnDestroy {
  constructor(
    private reposicionService: ReposicionService,
    private branchStoreService: BranchStoreService,
    private tablaMedicamentosService: TablaMedicamentosService
  ) {}

  private destroy$ = new Subject<void>();

  sucursalesSeleccionadas: string[] = [];

  medicamentos: Medicamento[] = [];

  inventarioMap = new Map<string, Inventario>();

  stockMap = new Map<string, number>();

  loading = true;

  filtroBusqueda = '';
  filtroTipo = 'todos';

  paginaActual = 1;
  elementosPorPagina = 8;

  productosCache: ProductoAPI[] = [];

  async ngOnInit(): Promise<void> {
    await this.cargarProductosAPI();

    /* sucursales */

    this.reposicionService.sucursalesSeleccionadas$
      .pipe(takeUntil(this.destroy$))
      .subscribe((sucursales) => {
        this.sucursalesSeleccionadas = sucursales;

        if (this.productosCache.length > 0) {
          this.construirInventario();
        }
      });

    /* filtros */

    this.reposicionService.filtroBusqueda$
      .pipe(takeUntil(this.destroy$))
      .subscribe((valor) => {
        this.filtroBusqueda = valor.toLowerCase();
        this.paginaActual = 1;
      });

    this.reposicionService.filtroTipo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((valor) => {
        this.filtroTipo = valor;
        this.paginaActual = 1;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ===============================
        CARGAR API
  =============================== */

  async cargarProductosAPI(): Promise<void> {
    this.loading = true;

    try {
      this.productosCache = [];
      this.stockMap.clear();

      const resp = await this.branchStoreService.getMatrixProducts(['44']);

      const productos: ProductoAPI[] = resp?.data?.[0]?.products ?? [];

      this.productosCache =
        this.tablaMedicamentosService.agruparProductos(productos);

      this.medicamentos = this.productosCache.map((p) => ({
        codigo: p.productCode,
        productId: p.productId,
        nombre: p.productName,
        rotacion: p.format
      }));

      this.stockMap = this.tablaMedicamentosService.construirStockMap(
        this.productosCache
      );

      if (this.sucursalesSeleccionadas.length > 0) {
        this.construirInventario();
      }
    } catch (error) {
      console.error('ERROR CARGANDO PRODUCTOS', error);
    } finally {
      this.loading = false;
    }
  }

  /* ===============================
        INVENTARIO
  =============================== */

  construirInventario(): void {
    this.inventarioMap = this.tablaMedicamentosService.construirInventario(
      this.productosCache,
      this.sucursalesSeleccionadas,
      this.reposicionService.inventario
    );

    this.syncInventarioService();
  }

  getKey(productId: string, sucursal: string): string {
    return `${productId}-${sucursal}`;
  }

  actualizarCantidad(productId: string, sucursal: string, cantidad: number) {
    const key = this.getKey(productId, sucursal);

    const registro = this.inventarioMap.get(key);

    if (!registro) return;

    registro.cantidad = cantidad;

    this.reposicionService.actualizarCantidad(productId, sucursal, cantidad);
  }

  /* ===============================
        FILTROS
  =============================== */

  get medicamentosFiltrados(): Medicamento[] {
    let lista = [...this.medicamentos];

    if (this.filtroBusqueda) {
      const filtro = this.filtroBusqueda;

      lista = lista.filter(
        (m) =>
          m.nombre.toLowerCase().includes(filtro) ||
          m.codigo.toLowerCase().includes(filtro)
      );
    }

    if (this.filtroTipo === 'stock-bajo') {
      lista = lista.filter((m) => this.obtenerStock(m.productId) < 50);
    }

    if (this.filtroTipo === 'alta-rotacion') {
      lista = lista.filter((m) => m.rotacion.toLowerCase().includes('alta'));
    }

    return lista;
  }

  /* ===============================
        PAGINACION
  =============================== */

  get medicamentosPaginados(): Medicamento[] {
    const inicio = (this.paginaActual - 1) * this.elementosPorPagina;

    return this.medicamentosFiltrados.slice(
      inicio,
      inicio + this.elementosPorPagina
    );
  }

  get totalPaginas(): number {
    return Math.ceil(
      this.medicamentosFiltrados.length / this.elementosPorPagina
    );
  }

  cambiarPagina(pagina: number): void {
    if (pagina < 1 || pagina > this.totalPaginas) return;

    this.paginaActual = pagina;
  }

  get medicamentosMostrados(): number {
    return this.medicamentosPaginados.length;
  }

  /* ===============================
        STOCK
  =============================== */

  obtenerStock(productId: string): number {
    return this.stockMap.get(productId) ?? 0;
  }

  obtenerCantidad(productId: string, sucursal: string): number {
    const key = this.getKey(productId, sucursal);

    return this.inventarioMap.get(key)?.cantidad ?? 0;
  }

  calcularTotal(productId: string): number {
    let total = 0;

    this.sucursalesSeleccionadas.forEach((sucursal) => {
      const key = this.getKey(productId, sucursal);

      total += this.inventarioMap.get(key)?.cantidad ?? 0;
    });

    return total;
  }

  trackByMedicamento(index: number, item: Medicamento) {
    return item.productId;
  }

  /* ===============================
        SYNC SERVICE
  =============================== */

  syncInventarioService(): void {
    const inventario = Array.from(this.inventarioMap.values());

    this.reposicionService.setInventario(inventario);
  }
}
